/**
 * The board `···` menu (spec M1-B1 §7.4; **order, wording and both submenus re-measured in
 * B10** from `[c:board-menu]`, `[c:board-menu-settings]`, `[c:board-menu-more-actions]`).
 *
 * monday's menu is **248 px** wide with 32 px rows and 17 px group gaps:
 *
 *   Power-Ups · AI suggestions · Convert board to project      ← monday-only, absent here
 *   ─
 *   Activity log · Discussion
 *   ─
 *   Notifications · Permissions · Settings ▸ · More actions ▸
 *   ─
 *   Archive board · Delete board
 *   ─
 *   View archive / trash
 *   ─
 *   Give feedback                                              ← monday-only, absent here
 *
 *   Settings ▸      Rename board · Add to favorites · Change board type · Default item values ·
 *                   Use card for item creation · Create items via email · Change item terminology ·
 *                   Archived history
 *   More actions ▸  Save as a template · Build a report from board · Export board to Excel ·
 *                   Import items · Duplicate board · Full screen
 *
 * Round 3 had guessed both submenus (it put `Move to` and `Edit description` under Settings and
 * `Copy board link` under More actions; monday has none of those there) — they are corrected
 * here. `Notifications`, `Permissions` and `View archive / trash` are **plain entries**, not
 * submenus: hovering each on monday opens nothing.
 */
import { useNavigate } from 'react-router';
import { useToast } from '@elizmonday/ui';
import { boardCanBeTemplate } from '@elizmonday/shared';
import { Menu, type MenuEntry } from '../board-view/table/menus/Menu';
import { boardViewUrl } from '../shell/urls';
import { useFavorite } from './favorites';
import { BOARD_KIND_LABEL, useBoard, useBoardMutations, type BoardKind } from './hooks';
import type { BoardDialog } from './BoardHeader';

export const BOARD_MENU_WIDTH = 248;

export function BoardMenu({
  boardId,
  viewId,
  x,
  y,
  leading = [],
  onClose,
  onArchive,
  onDelete,
  onOpenDialog,
  onPrint,
}: {
  boardId: string;
  viewId: string | null;
  x: number;
  y: number;
  /**
   * Entries prepended above monday's own, with a divider under them. Empty everywhere but a
   * phone, where P-042 §1 folds the header's widget row (Integrate, Automate, the owners,
   * Invite, the copy-link button) into this menu because the 639 px row cannot be reached in a
   * 326 px band. Desktop passes nothing and the menu is byte-identical to B10's capture.
   */
  leading?: MenuEntry[];
  onClose(): void;
  onArchive(): void;
  onDelete(): void;
  onOpenDialog(dialog: BoardDialog): void;
  /** P-090: `window.print()` with the grid unvirtualised — the board view owns the grid. */
  onPrint(): void;
}) {
  const board = useBoard(boardId);
  const mutations = useBoardMutations();
  const favorite = useFavorite('board', boardId);
  const navigate = useNavigate();
  const toast = useToast();
  const kind = (board.data?.kind ?? 'public') as BoardKind;

  const pick = (dialog: NonNullable<BoardDialog>) => () => {
    onClose();
    onOpenDialog(dialog);
  };

  const entries: MenuEntry[] = [
    ...(leading.length > 0 ? [...leading, { id: 'd0', label: '', divider: true } as MenuEntry] : []),
    {
      id: 'activity',
      label: 'Activity log',
      'data-testid': 'board-menu-activity',
      onSelect: () => {
        onClose();
        navigate(`${boardViewUrl(boardId, viewId ?? '')}/activity_log`);
      },
    },
    {
      id: 'discussion',
      label: 'Discussion',
      disabled: true,
      title: 'Board-level discussions are not part of this product; item updates are (B7).',
    },
    { id: 'd1', label: '', divider: true },
    {
      id: 'notifications',
      label: 'Notifications',
      'data-testid': 'board-menu-notifications',
      onSelect: pick('notifications'),
    },
    {
      id: 'permissions',
      label: 'Permissions',
      'data-testid': 'board-menu-permissions',
      onSelect: pick('permissions'),
    },
    {
      id: 'settings',
      label: 'Settings',
      submenu: [
        {
          id: 'rename',
          label: 'Rename board',
          onSelect: () => {
            onClose();
            document.querySelector<HTMLElement>('[data-testid="board-title"]')?.click();
          },
        },
        {
          id: 'favorite',
          label: favorite.isFavorite ? 'Remove from favorites' : 'Add to favorites',
          onSelect: () => {
            onClose();
            favorite.toggle();
          },
        },
        {
          id: 'kind',
          label: 'Change board type',
          submenu: (['public', 'share', 'private'] as BoardKind[]).map((next) => ({
            id: `kind-${next}`,
            label: BOARD_KIND_LABEL[next],
            disabled: next === kind,
            onSelect: () => {
              onClose();
              mutations.changeKind.mutate({ id: boardId, kind: next });
            },
          })),
        },
        {
          id: 'defaults',
          label: 'Default item values',
          disabled: true,
          title: 'Reserved settings key only — no UI until the column defaults land (Q173).',
        },
        {
          id: 'card-creation',
          label: 'Use card for item creation',
          disabled: true,
          title: 'monday opens the item card on create; this product creates inline (B3 §7).',
        },
        {
          id: 'email',
          label: 'Create items via email',
          disabled: true,
          title: 'Inbound email needs an MX endpoint; SMTP is outbound only today.',
        },
        {
          id: 'terminology',
          label: 'Change item terminology',
          'data-testid': 'board-menu-terminology',
          onSelect: pick('terminology'),
        },
        {
          id: 'archived-history',
          label: 'Archived history',
          disabled: true,
          title: 'The activity log is kept in full; monday archives rows older than its plan limit.',
        },
      ],
    },
    {
      /**
       * **P-090.** Nothing on the board chrome printed it: not the toolbar `···`, not this
       * menu, not a shortcut, and `Ctrl+P` gave the ~19 virtualised rows that happened to be
       * mounted inside the app shell. `printBoard()` renders every row and calls
       * `window.print()` behind `table.css`'s `@media print`. monday's own board-menu capture
       * has no Print row and its print output was never captured, so both *where* it belongs
       * and *what monday prints* are open — **q503**.
       */
      id: 'print',
      label: 'Print board',
      'data-testid': 'board-menu-print',
      onSelect: () => {
        onClose();
        onPrint();
      },
    },
    {
      id: 'more',
      label: 'More actions',
      submenu: [
        /**
         * monday greys `Save as a template` on a board that is not `public`, with its reason
         * as a tooltip — measured on the sandbox board, which is private
         * (`[c:board-menu-more-actions]` shows the row `disabled`). `MenuEntry.title` (added
         * in B10) finally lets ours say the same thing.
         */
        {
          id: 'template',
          label: 'Save as a template',
          disabled: !boardCanBeTemplate(kind),
          title: boardCanBeTemplate(kind) ? undefined : 'Only a main board can be saved as a template.',
          ...(boardCanBeTemplate(kind) ? { onSelect: pick('template') } : {}),
        },
        {
          id: 'report',
          label: 'Build a report from board',
          disabled: true,
          title: 'Builds a monday dashboard from the board; create a dashboard from + Add instead.',
        },
        { id: 'export', label: 'Export board to Excel', onSelect: pick('export') },
        {
          id: 'import',
          label: 'Import items',
          disabled: true,
          title: 'Excel import arrives with M12; the account migration importer is admin-only.',
        },
        { id: 'duplicate', label: 'Duplicate board', onSelect: pick('duplicate') },
        {
          /**
           * **P-091.** `MoveBoardDialog` and `boards.move` have both existed since B9 and the
           * only way in was the **sidebar** row menu — so a user working inside a board had to
           * find it in the tree first, and the migration's per-board tidy-up was a sidebar
           * exercise. One dialog, two entry points. monday's captured menu has no *Move to*
           * either, so the position (beside *Duplicate board*) is ours until **q510** answers.
           */
          id: 'move',
          label: 'Move to',
          'data-testid': 'board-menu-move',
          onSelect: pick('move'),
        },
        {
          id: 'full-screen',
          label: 'Full screen',
          onSelect: () => {
            onClose();
            void document.documentElement.requestFullscreen?.().catch(() => {
              toast('Full screen was refused by the browser', { kind: 'negative' });
            });
          },
        },
      ],
    },
    { id: 'd2', label: '', divider: true },
    { id: 'archive', label: 'Archive board', onSelect: () => { onClose(); onArchive(); } },
    { id: 'delete', label: 'Delete board', danger: true, onSelect: () => { onClose(); onDelete(); } },
    { id: 'd3', label: '', divider: true },
    {
      id: 'trash',
      label: 'View archive / trash',
      'data-testid': 'board-menu-archive-trash',
      /**
       * monday's is **board-scoped**: this board's archived and deleted *items*, not the
       * account-level Archive and Trash the avatar menu already has. 6f had to leave it
       * disabled because nothing listed them; 6i added `items.listRemoved` (q452) and this
       * opens the dialog over it.
       */
      onSelect: pick('archive-trash'),
    },
  ];

  return <Menu entries={entries} x={x} y={y} width={BOARD_MENU_WIDTH} label="Board options" onClose={onClose} />;
}
